import { memo, type ReactElement } from 'react';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import styles from '../stepper.module.css';

export interface StepProps {
  label: string;
  stepIndex?: number;
  totalSteps?: number;
  isActive?: boolean;
  isComplete?: boolean;
  isFirst?: boolean;
  onClick?: (stepIndex: number) => void;
}

function StepComponent({
  label,
  stepIndex = 0,
  totalSteps = 0,
  isActive,
  isComplete,
  onClick,
}: StepProps): ReactElement {
  const intl = useIntl();

  const isClickable = isComplete && !isActive && onClick && typeof stepIndex === 'number';
  const labelClassName = cx(
    styles.stepLabel,
    isActive && styles.active,
    isComplete && styles.completed
  );

  const renderLabel = () => {
    const labelIndex = `${stepIndex + 1}. `;
    return (
      <>
        <span className={styles.stepLabelIndex}>{labelIndex}</span>
        {label}
      </>
    );
  };

  return (
    <li
      aria-current={isActive ? 'step' : undefined}
      className={cx(styles.stepContainer, isComplete && styles.stepContainerComplete)}
      data-test-stepper-step={stepIndex}
    >
      {isClickable ? (
        <button
          className={cx(labelClassName, styles.stepButton)}
          onClick={() => {
            onClick(stepIndex);
          }}
          type="button"
        >
          {renderLabel()}
        </button>
      ) : (
        <span className={cx(labelClassName, !isActive && styles.stepLabelDesktop)}>
          {renderLabel()}
        </span>
      )}
      {isActive ? (
        <span className={cx(styles.stepLabelCount, 'body-2')}>
          {intl.formatMessage(
            { id: 'stepper.step.responsive-label', defaultMessage: 'Step {step}/{totalSteps}' },
            { step: stepIndex + 1, totalSteps }
          )}
        </span>
      ) : null}
    </li>
  );
}

StepComponent.displayName = 'Step';

export const Step = memo(StepComponent);
